import { Injectable } from '@angular/core';
import {
  isDefined,
  isNil,
  isNonEmptyString,
  TemplateStringable,
} from '@fmnts/core';
import * as Decimal from '@fmnts/core/decimal';
import {
  DeclarativeForm,
  DeclarativeFormComponent,
  FieldDeclaration,
  FieldPath,
  fieldsWithKeys,
  FormDeclaration,
  isFieldDeclaration,
  SelectInputFieldDeclaration,
  TemplateContext,
  TemplateExpression,
  TemplateInterpolator,
  TextboxFieldDeclaration,
} from '@fmnts/declarative-forms';
import { TranslationService } from '@fmnts/i18n';
import * as Fn from 'effect/Function';
import * as O from 'effect/Option';
import {
  debounceTime,
  distinctUntilChanged,
  EMPTY,
  filter,
  map,
  Observable,
  startWith,
  switchMap,
} from 'rxjs';

/**
 * Providers template interpolation for form fields.
 *
 * If an expression references a field, e.g. `{{ first_name }}`
 * this service will provide an observable interpolation
 * for this fields value.
 *
 * The exact value that the field interpolates to depends
 * on the widget of the field.
 */
@Injectable()
export class FormTemplateService implements TemplateInterpolator {
  constructor(private translationService: TranslationService) {}

  canCreate(expression: TemplateExpression, ctx: TemplateContext): boolean {
    const formComponent = this.getFormComponent(ctx);
    return (
      formComponent?.declaration?.findByKey(
        expression.expression,
        isFieldDeclaration,
      ) !== undefined
    );
  }

  expression$(
    expression: TemplateExpression,
    ctx: TemplateContext,
  ): Observable<string> {
    const formComponent = this.getFormComponent(ctx);

    return this.fieldValue$(
      expression,
      formComponent.form,
      formComponent.declaration,
    );
  }

  /**
   * @param templateExpression Matched template expression
   * @param form Form
   * @param declaration Form declaration
   *
   * @returns
   * An `Observable` that emits changes to fields
   * with the given `fieldKey`.
   */
  private fieldValue$(
    templateExpression: TemplateExpression,
    form: DeclarativeForm,
    declaration: FormDeclaration,
  ): Observable<string> {
    const { expression: fieldKey } = templateExpression;

    // Get map of all fields that are referenced by the expression
    const fields: ReadonlyMap<FieldPath, FieldDeclaration> = new Map(
      declaration.findAllPaths(fieldsWithKeys(fieldKey)).map((path) => {
        const field = declaration.get(path);

        if (!field) {
          throw new Error(`Could not find field with path ${path}`);
        }
        return [path, field];
      }),
    );

    // Without any fields, there is nothing to emit.
    if (fields.size === 0) {
      return EMPTY;
    }

    // Get a stream that emits with the active field and control
    const activeFieldControl$ = form.valueChanges.pipe(
      debounceTime(75),
      startWith(form.value),
      map(findActiveControl(form, fields)),
      filter(isDefined),
      // Dont emit new values, if the active field hasn't changed
      distinctUntilChanged(
        (prev, cur) => prev.field === cur.field && prev.control === cur.control,
      ),
    );

    return activeFieldControl$.pipe(
      switchMap(({ field, control }) =>
        control.valueChanges.pipe(
          startWith(control.value),
          distinctUntilChanged(),
          map((value) => this._mapFieldValue(value, field)),
        ),
      ),
    );
  }

  /**
   * Maps the `currentValue` of a `field` to a value
   * that makes sense for the fields widget.
   *
   * @param currentValue Current value of the field
   * @param field The field itself
   *
   * @returns
   * A mapped value that best represents the `currentValue`
   * dependening on the fields `widget`.
   */
  private _mapFieldValue(
    currentValue: unknown,
    field: FieldDeclaration,
  ): string {
    // TODO(arch): the form-template service should not directly know about next field keys
    // Special handling for amount fields
    switch (field.key) {
      case 'donation_amount_per_interval':
      case 'donation_amount':
        return _decimalLikeToString(currentValue);
    }

    // TODO(arch): the form-template service should not directly know about field widgets
    switch (field.widget) {
      case 'amountChoice':
      case 'multiIntervalAmountChoice':
        return _decimalLikeToString(currentValue);
      case 'radioToggle':
      case 'select':
      case 'sponsorship':
        const items = (field.widgetOptions as SelectInputFieldDeclaration)
          .items;
        const item = items.find(
          isNil(currentValue)
            ? ({ value }) => isNil(value)
            : ({ value }) =>
                (currentValue as TemplateStringable).toString() ===
                (value as TemplateStringable).toString(),
        );

        // No item was found, return empty string
        if (!item) {
          return '';
        }

        // Special handling for "currency" select field
        if (field.key === 'currency') {
          return typeof item.symbol === 'string' ? item.symbol : '';
        }

        // In all other cases, return the translated item label
        return this.translationService.getInlineTranslation(item.label);

      case 'textbox':
        return this.translationService.getInlineTranslation(
          (field.widgetOptions as TextboxFieldDeclaration).text,
        );
      default:
        return currentValue as string;
    }
  }

  private getFormComponent(ctx: TemplateContext) {
    return ctx.injector.get(DeclarativeFormComponent);
  }
}

/**
 * Helper to find the active control in a form
 * from a map of fields.
 */
function findActiveControl(
  form: DeclarativeForm,
  fields: ReadonlyMap<[string, string], FieldDeclaration>,
) {
  return () => {
    for (const [path, field] of fields) {
      const control = form.get(path);
      if (control?.enabled) {
        return { control, field, path };
      }
    }
    return undefined;
  };
}

const constEmptyString = Fn.constant('' as const);

function _decimalLikeToString(value: unknown): string {
  return Decimal.fromUnknown(value).pipe(
    O.map((d) => d.toDecimalPlaces(2).toFixed(2)),
    O.getOrElse(() =>
      Fn.pipe(
        value,
        O.liftPredicate(isNonEmptyString),
        O.getOrElse(constEmptyString),
      ),
    ),
  );
}
